import React from 'react';
import { CheckCircle2, Circle, Loader2 } from 'lucide-react';

/**
 * ProgressPanel - Right sidebar showing progress through 7 sections
 */
export default function ProgressPanel({
  sections,
  currentIndex,
  sectionsCompleted,
  sectionData,
  onNavigate,
  isSectionComplete
}) {
  return (
    <div className="p-6 space-y-6">
      {/* Progress Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Progress</h3>
        <div className="flex items-center space-x-2">
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(sectionsCompleted.length / sections.length) * 100}%` }}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">
            {sectionsCompleted.length}/{sections.length}
          </span>
        </div>
      </div>

      {/* Section List */}
      <div className="space-y-2">
        {sections.map((section, index) => {
          const isComplete = isSectionComplete(section.key);
          const isCurrent = index === currentIndex;
          const isClickable = true; // Allow navigation to any section

          return (
            <button
              key={section.key}
              onClick={() => isClickable && onNavigate(index)}
              disabled={!isClickable}
              className={`
                w-full text-left p-3 rounded-lg transition-all
                ${isCurrent
                  ? 'bg-indigo-50 border-2 border-indigo-600'
                  : isComplete
                    ? 'bg-white border-2 border-green-200 hover:border-green-300'
                    : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                }
                ${!isClickable && 'opacity-50 cursor-not-allowed'}
              `}
            >
              <div className="flex items-start space-x-3">
                {/* Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {isComplete ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : isCurrent ? (
                    <Loader2 className="w-5 h-5 text-indigo-600 animate-pulse" />
                  ) : (
                    <Circle className="w-5 h-5 text-gray-400" />
                  )}
                </div>

                {/* Label */}
                <div className="flex-1 min-w-0">
                  <div className={`
                    text-sm font-medium
                    ${isCurrent ? 'text-indigo-900' : isComplete ? 'text-gray-900' : 'text-gray-600'}
                  `}>
                    {section.label}
                  </div>

                  {/* Field count indicator */}
                  {!isComplete && !isCurrent && (
                    <div className="text-xs text-gray-500 mt-1">
                      {getFieldCompletionText(section.key, sectionData[section.key])}
                    </div>
                  )}

                  {isComplete && (
                    <div className="text-xs text-green-600 mt-1">
                      ✓ Complete
                    </div>
                  )}

                  {isCurrent && !isComplete && (
                    <div className="text-xs text-indigo-600 mt-1">
                      In progress...
                    </div>
                  )}
                </div>

                {/* Section number */}
                <div className={`
                  flex-shrink-0 text-xs font-semibold
                  ${isCurrent ? 'text-indigo-600' : 'text-gray-400'}
                `}>
                  {index + 1}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Tips */}
      <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Tip</h4>
        <p className="text-sm text-blue-800">
          {getTipForSection(sections[currentIndex].key)}
        </p>
      </div>
    </div>
  );
}

// Helper: Get field completion text
function getFieldCompletionText(sectionKey, data) {
  if (!data) return 'Not started';

  switch (sectionKey) {
    case 'ethos':
      const ethosFields = [
        data.motto,
        data.motto_in_action,
        data.buzzwords?.length > 0
      ].filter(Boolean).length;
      return `${ethosFields}/3 fields`;

    case 'academics':
      const academicFields = [
        data.courses?.length > 0,
        data.professors?.length > 0,
        data.initiatives?.length > 0
      ].filter(Boolean).length;
      return `${academicFields}/3 categories`;

    case 'campus':
      const campusFields = [
        data.spaces?.length > 0,
        data.clubs?.length > 0
      ].filter(Boolean).length;
      return `${campusFields}/2 categories`;

    case 'personal':
      const personalFields = [
        data.micro_moment,
        data.core_motivations?.length >= 2
      ].filter(Boolean).length;
      return `${personalFields}/2 fields`;

    case 'rhetoric':
      const rhetoricFields = [
        data.contrast,
        data.mission_metaphor,
        data.what_its_not
      ].filter(Boolean).length;
      return `${rhetoricFields}/3 devices`;

    case 'bridges':
      return `${data.rows?.length || 0}/2 bridges min`;

    case 'compose':
      return data.style ? 'Style selected' : 'Not started';

    default:
      return 'Not started';
  }
}

// Helper: Get contextual tip for current section
function getTipForSection(sectionKey) {
  const tips = {
    ethos: 'Look for the school\'s motto or mission statement on their website. What does it reveal about their values?',
    academics: 'Search the course catalog for specific classes that connect to your interests. Be concrete!',
    campus: 'Think beyond buildings—what behaviors or interactions does the campus culture encourage?',
    personal: 'Choose a micro-moment that reveals how you think, not just what you accomplished.',
    rhetoric: 'The best rhetorical devices show contrast: how THIS school is different from others.',
    bridges: 'Strong bridges connect a specific school element to a specific personal experience. Be concrete on both ends.',
    compose: 'Your closing line should be memorable and impossible to swap to another school.'
  };

  return tips[sectionKey] || 'Complete each section to build a compelling essay.';
}
